import React, {
  createContext,
  ReactNode,
  useEffect,
  useRef,
  useState
} from "react";
import { fetchAllDepartments } from "../../models/products.model";
import { requestNewRefreshToken } from "../../models/signup.model";
import { isDepartment } from "../../utilities/dataValidators";
import {
  decodeUserJWT,
  getMilliseconds,
  getUserLanguage,
  JWTIsExpired,
  JWTNotGoingToExpire,
  utilLogOut
} from "../../utilities/utils";

export type UserState = {
  uuid: string;
  email: string;
  nickname: string;
  bidToken: string;
  profileIcon?: string;
  mobileNumber: string;
} | null;

export type UserContextType = {
  user: UserState;
  setUser: (user: UserState) => void;
  isLoggedIn: boolean;
  productDepartments: Department[];
  setProductDepartments: (departments: Department[]) => void;
  socketAuthenticated: boolean;
  setSocketAuthenticated: (status: boolean) => void;
  numBids: number;

  setNumBids: (numBids: number) => void;
  showLoginModal: boolean;
  setShowLoginModal: (show: boolean) => void;
  showSignupModal: boolean;
  setShowSignupModal: (show: boolean) => void;
  shareLink: string;
  setShareLink: (link: string) => void;
  loadingDepartments: boolean;
  favoriteProducts: number[];
  setFavoriteProducts: (favoriteProducts: number[]) => void;
  cartItems: CartItem[] | null; // null means the cart is not loaded yet
  setCartItems: (items: CartItem[]) => void;
  showChatBox: boolean;
  setShowChatBox: (show: boolean) => void;
  showChatNotificationModal: boolean;
  setShowChatNotificationModal: (show: boolean) => void;
  currentPage: string;
  setCurrentPage: (page: string) => void;
  aiSystemMessage: string | null;
  notificationPositionTop: boolean;
  setNotificationPositionTop: (position: boolean) => void;
  setAiSystemMessage: (newMessage: string | null) => void;
  aiMessages: ChatMessage[] | null;
  setAiMessages: (messages: ChatMessage[] | null) => void;
  timeoutRef: React.MutableRefObject<number | null>;
  hideNotificationTimeOutRef: React.MutableRefObject<number | null>;
};

const defaultState: UserContextType = {
  user: null,
  setUser: () => {}, // default empty implementation
  isLoggedIn: false,
  productDepartments: [],
  setProductDepartments: () => {}, // default empty implementation,
  socketAuthenticated: false,
  setSocketAuthenticated: () => {},
  numBids: 0,
  setNumBids: () => {},
  showLoginModal: false,
  setShowLoginModal: () => {},
  showSignupModal: false,
  setShowSignupModal: () => {},
  shareLink: "",
  setShareLink: () => {},
  loadingDepartments: true,
  favoriteProducts: [],
  setFavoriteProducts: () => {},
  cartItems: null,
  setCartItems: () => {},
  showChatBox: false,
  setShowChatBox: (show: boolean) => {},
  showChatNotificationModal: false,
  setShowChatNotificationModal: (show: boolean) => {},
  currentPage: "",
  setCurrentPage: (page: string) => {},
  aiSystemMessage: "",
  setAiSystemMessage: (newMessage: string | null) => {},
  notificationPositionTop: false,
  setNotificationPositionTop: (position: boolean) => {},
  aiMessages: null,
  setAiMessages: (messages: ChatMessage[] | null) => {},
  timeoutRef: { current: null },
  hideNotificationTimeOutRef: { current: null }
};

export const UserContext = createContext<UserContextType>(defaultState);

interface UserProviderProps {
  children: ReactNode;
}

const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  // get from userInfo in local storage
  const [user, setUser] = useState<UserState>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [productDepartments, setProductDepartments] = useState<Department[]>(
    []
  );
  const [loadingDepartments, setLoadingDepartments] = useState<boolean>(true);
  const [socketAuthenticated, setSocketAuthenticated] = useState(false);
  const [numBids, setNumBids] = useState<UserContextType["numBids"]>(
    defaultState.numBids
  );
  const [showLoginModal, setShowLoginModal] = useState<boolean>(
    defaultState.showLoginModal
  );
  const [showSignupModal, setShowSignupModal] = useState<boolean>(
    defaultState.showSignupModal
  );
  const [shareLink, setShareLink] = useState<string>("");
  const [favoriteProducts, setFavoriteProducts] = useState<number[]>([]);
  const [cartItems, setCartItems] = useState<CartItem[] | null>(null); // null means the cart is not loaded yet

  const [showChatBox, setShowChatBox] = useState<boolean>(false);
  const [showChatNotificationModal, setShowChatNotificationModal] =
    useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<string>("");
  const [aiSystemMessage, setAiSystemMessage] = useState<string | null>(null);
  const [aiMessages, setAiMessages] = useState<ChatMessage[] | null>(null);
  // move notification to the top of the screen - controlled by the top nav bar for central control
  // for notification position matching up when tablet / mobile navbar disappears
  const [notificationPositionTop, setNotificationPositionTop] =
    useState<boolean>(false);
  // control delay for ai message notification
  const timeoutRef = useRef<number | null>(null);
  const hideNotificationTimeOutRef = useRef<number | null>(null);

  useEffect(() => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      return;
    }
    const userData = decodeUserJWT(refreshToken);
    // console.log(userData, "userData");
    if (userData) {
      setUser(userData);
    }
  }, []);

  useEffect(() => {
    if (loading) {
      return;
    }

    setLoadingDepartments(true);

    // fetch all departments from local storage, with an expiration of 1 day
    const departments = localStorage.getItem("departments");
    if (departments) {
      const { timestamp, data, lang } = JSON.parse(departments);
      const maxAge = getMilliseconds({
        d: 0,
        h: 0,
        m: 10,
        s: 0
      });
      if (Date.now() - timestamp < maxAge && lang === getUserLanguage()) {
        // console.log("last fetched departments: ", Date.now() - timestamp);
        if (Array.isArray(data) && data.every(isDepartment)) {
          setProductDepartments(data);
          setLoadingDepartments(false);
          return;
        }
      }
      localStorage.removeItem("departments");
    }
    fetchAllDepartments().then((response) => {
      if (response.rStatus === "success") {
        setProductDepartments(response.rData);
        localStorage.setItem(
          "departments",
          JSON.stringify({
            timestamp: Date.now(),
            data: response.rData,
            lang: getUserLanguage()
          })
        );
      }
      setLoadingDepartments(false);
    });
  }, [loading]);

  useEffect(() => {
    if (user) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
    setLoading(false);
  }, [user]);

  // run a check for the refresh token every 5 minutes
  useEffect(() => {
    const checkTokenTimeSeconds = 60 * 60; // 1 hour
    const interval = setInterval(() => {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        return;
      }

      if (JWTIsExpired(refreshToken)) {
        console.log("Token has expired");
        utilLogOut();
        return;
      }

      // check if the token is going to expire soon
      if (JWTNotGoingToExpire(refreshToken, checkTokenTimeSeconds * 2.5)) {
        return;
      }

      console.log("Token is going to expire soon");

      requestNewRefreshToken(refreshToken).then((response) => {
        console.log(response, "response");
        if (response.rStatus === "success") {
          const newRefreshToken = response.rData;
          const newUserData = decodeUserJWT(newRefreshToken);
          if (newUserData) {
            localStorage.setItem("refreshToken", newRefreshToken);
            setUser(newUserData);
          } else {
            console.error("JWT could not be decoded");
            utilLogOut();
          }
        } else {
          utilLogOut();
        }
      });
    }, checkTokenTimeSeconds * 1000);

    return () => clearInterval(interval);
  }, []);

  return loading ? (
    <></>
  ) : (
    <UserContext.Provider
      value={{
        user,
        setUser,
        isLoggedIn,
        productDepartments,
        setProductDepartments,
        socketAuthenticated,
        setSocketAuthenticated,
        numBids,
        setNumBids,
        showLoginModal,
        setShowLoginModal,
        showSignupModal,
        setShowSignupModal,
        shareLink,
        setShareLink,
        loadingDepartments,
        favoriteProducts,
        setFavoriteProducts,
        cartItems,
        setCartItems,
        showChatBox,
        setShowChatBox,
        showChatNotificationModal,
        setShowChatNotificationModal,
        currentPage,
        setCurrentPage,
        aiSystemMessage,
        setAiSystemMessage,
        aiMessages,
        setAiMessages,
        notificationPositionTop,
        setNotificationPositionTop,
        timeoutRef,
        hideNotificationTimeOutRef
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider;
